import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as notificationApi from '../../api/notification';
import { message } from 'antd';

// 获取通知列表
export const fetchNotifications = createAsyncThunk(
  'notification/fetchNotifications',
  async (params, { rejectWithValue }) => {
    try {
      const response = await notificationApi.getNotificationList(params);
      if (response.code === 200) {
        return response.data;
      }
      return rejectWithValue(response.message || '获取通知列表失败');
    } catch (error) {
      return rejectWithValue(error.message || '获取通知列表失败');
    }
  }
);

// 获取管理员个人通知列表
export const fetchAdminNotificationList = createAsyncThunk(
  'notification/fetchAdminList',
  async (queryDTO, { rejectWithValue, getState }) => {
    try {
      const response = await notificationApi.listAdminNotifications(queryDTO);
      
      if (!response || !response.data) {
        return rejectWithValue('获取管理员通知列表失败');
      }
      
      // 获取当前页码
      const currentPage = response.data.current || 1;
      
      return {
        data: response.data.records || [],
        total: response.data.total || 0,
        current: currentPage,
        pageSize: response.data.size || 10,
        pages: response.data.pages || 0,
        isLoadMore: currentPage > 1  // 标记是否为加载更多
      };
    } catch (error) {
      return rejectWithValue(error.message || '获取管理员通知列表失败');
    }
  }
);

// 创建通知
export const createNotification = createAsyncThunk(
  'notification/createNotification',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await notificationApi.createNotification(data);
      if (response.code === 200) {
        message.success('创建通知成功');
        // 刷新列表
        dispatch(fetchNotifications({ pageNum: 1, pageSize: 10 }));
        return response.data;
      }
      return rejectWithValue(response.message || '创建通知失败');
    } catch (error) {
      return rejectWithValue(error.message || '创建通知失败');
    }
  }
);

// 标记通知为已读
export const markNotificationAsRead = createAsyncThunk(
  'notification/markAsRead',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await notificationApi.markAsRead(id);
      if (response.code === 200) {
        message.success('已标记为已读');
        // 刷新列表
        dispatch(fetchAdminNotificationList({
          pageNum: 1,
          pageSize: 10,
          isRead: false
        }));
        return id;
      }
      return rejectWithValue(response.message || '标记已读失败');
    } catch (error) {
      return rejectWithValue(error.message || '标记已读失败');
    }
  }
);

// 标记所有通知为已读
export const markAllNotificationsAsRead = createAsyncThunk(
  'notification/markAllAsRead',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await notificationApi.markAllNotificationsAsRead();
      if (response.code === 200) {
        message.success('全部标记为已读');
        // 刷新列表
        dispatch(fetchAdminNotificationList({
          pageNum: 1,
          pageSize: 10,
          isRead: false
        }));
        return true;
      }
      return rejectWithValue(response.message || '标记所有通知为已读失败');
    } catch (error) {
      return rejectWithValue(error.message || '标记所有通知为已读失败');
    }
  }
);

// 删除通知
export const deleteNotification = createAsyncThunk(
  'notification/deleteNotification',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await notificationApi.deleteNotification(id);
      if (response.code === 200) {
        message.success('删除通知成功');
        // 刷新列表
        dispatch(fetchNotifications({ pageNum: 1, pageSize: 10 }));
        return id;
      }
      return rejectWithValue(response.message || '删除通知失败');
    } catch (error) {
      return rejectWithValue(error.message || '删除通知失败');
    }
  }
);

const initialState = {
  // 通用通知列表
  notifications: [],
  total: 0,
  current: 1,
  pageSize: 10,
  loading: false,
  error: null,

  // 管理员个人通知列表
  adminNotifications: {
    notifications: [],
    total: 0,
    current: 1,
    pageSize: 10,
    loading: false,
    error: null,
    totalPages: 0
  },

  currentNotification: null,
};

const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    setCurrentNotification: (state, action) => {
      state.currentNotification = action.payload;
    },
    clearCurrentNotification: (state) => {
      state.currentNotification = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 获取通知列表
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload.records;
        state.total = action.payload.total;
        state.current = action.payload.current;
        state.pageSize = action.payload.size;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        message.error(action.payload || '获取通知列表失败');
      })

      // 获取管理员个人通知列表
      .addCase(fetchAdminNotificationList.pending, (state) => {
        state.adminNotifications.loading = true;
        state.adminNotifications.error = null;
      })
      .addCase(fetchAdminNotificationList.fulfilled, (state, action) => {
        state.adminNotifications.loading = false;
        
        // 判断是否为加载更多
        if (action.payload.isLoadMore) {
          // 合并通知列表，去重
          const mergedNotifications = [
            ...state.adminNotifications.notifications,
            ...action.payload.data
          ];
          
          // 去重（基于 id）
          const uniqueNotifications = Array.from(
            new Map(
              mergedNotifications.map(item => [item.id, item])
            ).values()
          );
          
          state.adminNotifications.notifications = uniqueNotifications;
        } else {
          // 第一页直接替换
          state.adminNotifications.notifications = action.payload.data;
        }
        
        // 更新其他状态
        state.adminNotifications.total = action.payload.total;
        state.adminNotifications.current = action.payload.current;
        state.adminNotifications.totalPages = action.payload.pages;
        state.adminNotifications.pageSize = action.payload.pageSize;
      })
      .addCase(fetchAdminNotificationList.rejected, (state, action) => {
        state.adminNotifications.loading = false;
        state.adminNotifications.error = action.payload;
        message.error(action.payload || '获取管理员通知列表失败');
      })
      
      // 创建通知
      .addCase(createNotification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNotification.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createNotification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        message.error(action.payload || '创建通知失败');
      })
      
      // 标记已读
      .addCase(markNotificationAsRead.pending, (state) => {
        state.adminNotifications.loading = true;
        state.adminNotifications.error = null;
      })
      .addCase(markNotificationAsRead.fulfilled, (state) => {
        state.adminNotifications.loading = false;
      })
      .addCase(markNotificationAsRead.rejected, (state, action) => {
        state.adminNotifications.loading = false;
        state.adminNotifications.error = action.payload;
        message.error(action.payload || '标记已读失败');
      })
      
      // 标记所有通知为已读
      .addCase(markAllNotificationsAsRead.pending, (state) => {
        state.adminNotifications.loading = true;
        state.adminNotifications.error = null;
      })
      .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
        state.adminNotifications.loading = false;
      })
      .addCase(markAllNotificationsAsRead.rejected, (state, action) => {
        state.adminNotifications.loading = false;
        state.adminNotifications.error = action.payload;
        message.error(action.payload || '标记所有通知为已读失败');
      })
      
      // 删除通知
      .addCase(deleteNotification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteNotification.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteNotification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        message.error(action.payload || '删除通知失败');
      });
  },
});

export const { setCurrentNotification, clearCurrentNotification } = notificationSlice.actions;

export default notificationSlice.reducer; 